import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Activity, Save, X } from "lucide-react";
import { useState } from "react";
import { api } from "../../lib/api";
import { formatTimestamp } from "../../lib/format";
import { cn } from "../../lib/utils";
import { SectionTitle } from "./shared";

type EntsoeTestStatus = "ok" | "no_data" | "auth_failed" | "network_error" | "error" | null;

type EntsoeTokenStatus = {
  configured: boolean;
  last4: string | null;
  serverFallbackAvailable: boolean;
  lastTestedAt: string | null;
  lastTestStatus: EntsoeTestStatus;
  lastTestError: string | null;
  updatedAt: string | null;
};

const ENTSOE_TEST_LABELS: Record<Exclude<EntsoeTestStatus, null>, string> = {
  ok: "OK",
  no_data: "no data for probe date",
  auth_failed: "invalid token",
  network_error: "network error",
  error: "error",
};

type DamRefreshResult = {
  deliveryDate: string;
  source: string;
  status: "complete" | "partial" | "empty" | "error";
  expectedQuarters: number;
  normalizedRowCount: number;
  warnings: string[];
  error: string | null;
};

function isoDay(offsetDays: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

export function EntsoeTokenSection({
  isGlobalAdmin,
  activeCompanyId,
  companyIdOverride = null,
}: {
  isGlobalAdmin: boolean;
  activeCompanyId: string | null;
  companyIdOverride?: string | null;
}) {
  const queryClient = useQueryClient();
  const [token, setToken] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fromDate, setFromDate] = useState(() => isoDay(-14));
  const [backfillResults, setBackfillResults] = useState<DamRefreshResult[] | null>(null);

  const enabled = !isGlobalAdmin || Boolean(activeCompanyId);
  const tokenQ = useQuery({
    queryKey: ["admin", "entsoe-token", activeCompanyId],
    queryFn: () => api<{ status: EntsoeTokenStatus; canEdit: boolean }>(
      "/entsoe/token",
      companyRequest(companyIdOverride)
    ),
    enabled,
    retry: false,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["admin", "entsoe-token"] });

  const saveM = useMutation({
    mutationFn: (value: string) =>
      api<unknown>("/entsoe/token", companyRequest(companyIdOverride, {
        method: "PUT",
        body: JSON.stringify({ token: value }),
      })),
    onSuccess: () => {
      setToken("");
      setFeedback("Token saved.");
      setError(null);
      void invalidate();
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });
  const clearM = useMutation({
    mutationFn: () => api<unknown>("/entsoe/token", companyRequest(companyIdOverride, { method: "DELETE" })),
    onSuccess: () => {
      setToken("");
      setFeedback("Token removed.");
      setError(null);
      void invalidate();
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });
  const testM = useMutation({
    mutationFn: () =>
      api<{ status: Exclude<EntsoeTestStatus, null>; error: string | null; deliveryDate: string; rows: number }>(
        "/entsoe/token/test",
        companyRequest(companyIdOverride, { method: "POST" })
      ),
    onSuccess: (res) => {
      setError(null);
      setFeedback(
        res.status === "ok"
          ? `Live test OK — ENTSO-E returned ${res.rows} quarter-hours for ${res.deliveryDate}.`
          : `Live test: ${ENTSOE_TEST_LABELS[res.status]}${res.error ? ` — ${res.error}` : ""}`
      );
      void invalidate();
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });
  const backfillM = useMutation({
    mutationFn: (from: string) =>
      api<{ ok: boolean; results: DamRefreshResult[] }>("/forecasting/opcom-dam/refresh", companyRequest(companyIdOverride, {
        method: "POST",
        body: JSON.stringify({ fromDate: from, toDate: isoDay(0) }),
      })),
    onSuccess: (res) => {
      setError(null);
      setBackfillResults(res.results);
      const filled = res.results.filter((r) => r.status === "complete").length;
      setFeedback(`Backfill done: ${filled}/${res.results.length} days now complete.`);
      void queryClient.invalidateQueries({ queryKey: ["admin", "entsoe-token"] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  const status = tokenQ.data?.status;
  const canEdit = tokenQ.data?.canEdit ?? false;
  const busy = saveM.isPending || clearM.isPending || testM.isPending || backfillM.isPending;

  return (
    <section className="space-y-3">
      <SectionTitle icon={<Activity size={18} />} title="ENTSO-E day-ahead API token" />
      <p className="max-w-3xl text-xs text-muted">
        The day-ahead (DAM) price importer fetches from the ENTSO-E Transparency Platform first, falling
        back to OPCOM. Prices are shared across the platform, so one valid token keeps the daily refresh
        working for everyone. Get a token at{" "}
        <a
          href="https://transparency.entsoe.eu"
          target="_blank"
          rel="noreferrer"
          className="text-accent hover:underline"
        >
          transparency.entsoe.eu
        </a>{" "}
        → My Account Settings → Web API Security Token.
      </p>

      {isGlobalAdmin && !activeCompanyId ? (
        <div className="text-sm text-muted">Select a company to manage its ENTSO-E token.</div>
      ) : tokenQ.isLoading ? (
        <div className="text-xs text-muted">loading…</div>
      ) : tokenQ.isError ? (
        <div className="text-sm text-negRed">Failed to load token: {(tokenQ.error as Error).message}</div>
      ) : status ? (
        <div className="ops-panel ops-admin-token-panel max-w-2xl rounded-lg border border-border p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="text-sm">
              {status.configured ? (
                <span>
                  Token set
                  {status.last4 ? <span className="text-muted"> · ends in …{status.last4}</span> : null}
                </span>
              ) : status.serverFallbackAvailable ? (
                <span className="text-muted">No company token — using the server fallback token</span>
              ) : (
                <span className="text-amber">No token configured — importer falls back to OPCOM</span>
              )}
            </div>
            {status.lastTestStatus ? (
              <span
                className={cn(
                  "rounded border px-2 py-0.5 text-xs",
                  status.lastTestStatus === "ok"
                    ? "border-posGreen/40 text-posGreen"
                    : status.lastTestStatus === "no_data"
                      ? "border-amber/40 text-amber"
                      : "border-negRed/40 text-negRed"
                )}
              >
                {ENTSOE_TEST_LABELS[status.lastTestStatus]}
              </span>
            ) : null}
          </div>

          <label className="mb-1 block text-xs text-muted">
            {status.configured ? "Replace token" : "Web API security token"}
          </label>
          <input
            type="password"
            value={token}
            disabled={!canEdit || busy}
            onChange={(e) => setToken(e.target.value)}
            autoComplete="new-password"
            placeholder={status.configured ? "enter a new token to replace" : "paste your ENTSO-E token"}
            className="w-full rounded border border-border bg-transparent px-2 py-1.5 text-sm text-fg focus:border-accent focus:outline-none disabled:text-muted"
          />

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={!canEdit || busy || token.trim().length === 0}
              onClick={() => saveM.mutate(token.trim())}
              className="inline-flex items-center gap-1.5 rounded bg-accent px-3 py-1.5 text-sm text-bg disabled:opacity-40"
            >
              <Save size={14} /> Save
            </button>
            <button
              type="button"
              disabled={!canEdit || busy || !status.configured}
              onClick={() => testM.mutate()}
              className="inline-flex items-center gap-1.5 rounded border border-border px-3 py-1.5 text-sm text-fg disabled:opacity-40"
            >
              <Activity size={14} /> {testM.isPending ? "Testing…" : "Test key"}
            </button>
            <button
              type="button"
              disabled={!canEdit || busy || !status.configured}
              onClick={() => clearM.mutate()}
              className="inline-flex items-center gap-1.5 rounded border border-border px-3 py-1.5 text-sm text-negRed disabled:opacity-40"
            >
              <X size={14} /> Remove
            </button>
          </div>

          {status.lastTestedAt ? (
            <p className="mt-3 text-xs text-muted">
              Last test {formatTimestamp(status.lastTestedAt)}
              {status.lastTestError ? ` · ${status.lastTestError}` : ""}
            </p>
          ) : null}
          {feedback ? <p className="mt-2 text-xs text-posGreen">{feedback}</p> : null}
          {error ? <p className="mt-2 text-xs text-negRed">{error}</p> : null}
          {!canEdit ? (
            <p className="mt-2 text-xs text-muted">Read-only — a company admin can change this token.</p>
          ) : null}

          <div className="mt-4 border-t border-border/60 pt-3">
            <div className="text-xs font-medium text-fg">Backfill missing day-ahead prices</div>
            <p className="mt-1 max-w-2xl text-xs text-muted">
              Fetch any missing days from the chosen date through today using this token — use it to fill
              the gap the OPCOM outage left. Going forward, the cron self-heals the last 14 days
              automatically.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                disabled={busy}
                className="rounded border border-border bg-transparent px-2 py-1.5 text-sm text-fg"
              />
              <button
                type="button"
                disabled={busy || !status.configured || !fromDate}
                onClick={() => {
                  setBackfillResults(null);
                  backfillM.mutate(fromDate);
                }}
                className="inline-flex items-center gap-1.5 rounded border border-border px-3 py-1.5 text-sm text-fg disabled:opacity-40"
              >
                <Activity size={14} /> {backfillM.isPending ? "Backfilling…" : "Backfill missing days"}
              </button>
            </div>
            {backfillResults ? (
              <div className="ops-table-frame mt-3 max-h-72 overflow-auto rounded border border-border/60">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-bg text-muted">
                    <tr>
                      <th className="px-2 py-1 text-left font-medium">Date</th>
                      <th className="px-2 py-1 text-left font-medium">Source</th>
                      <th className="px-2 py-1 text-left font-medium">Status</th>
                      <th className="px-2 py-1 text-left font-medium">Rows</th>
                      <th className="px-2 py-1 text-left font-medium">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backfillResults.map((r) => (
                      <tr key={r.deliveryDate} className="border-t border-border/40">
                        <td className="px-2 py-1">{r.deliveryDate}</td>
                        <td className="px-2 py-1">{r.source}</td>
                        <td
                          className={cn(
                            "px-2 py-1",
                            r.status === "complete"
                              ? "text-posGreen"
                              : r.status === "error"
                                ? "text-negRed"
                                : "text-amber"
                          )}
                        >
                          {r.status}
                        </td>
                        <td className="px-2 py-1">
                          {r.normalizedRowCount}/{r.expectedQuarters}
                        </td>
                        <td className="px-2 py-1 text-muted">{r.error ?? r.warnings[0] ?? ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function companyRequest(companyId: string | null, init: RequestInit = {}): RequestInit {
  return companyId
    ? { ...init, headers: { ...(init.headers as Record<string, string> | undefined), "x-company-id": companyId } }
    : init;
}
